import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import "./App.css";
import { Toaster } from "react-hot-toast";
import { supabase } from "./supabase/supabase";
import { ADMIN_EMAILS } from "./constants";
// Page/component imports
const AcademyRegi = lazy(() => import("./pages/academyAdmin/AcademyRegi"));
const AthleteMain = lazy(() => import("./pages/athlete/AthleteMain"));
const Login = lazy(() => import("./pages/athlete/AthleteLogin"));
const AthRegi = lazy(() => import("./pages/athlete/AthleteRegi"));
const AdminLogin = lazy(() => import("./pages/superAdmin/AdminLogin"));
const Main = lazy(() => import("./pages/superAdmin/Main"));
const AcademyLogin = lazy(() => import("./pages/academyAdmin/AcademyLogin"));
const AcademyMain = lazy(() => import("./pages/academyAdmin/AcademyMain"));
const Report = lazy(() => import("./pages/common/reports/Report"));
const Start = lazy(() => import("./pages/public/Start"));
const CommonStart = lazy(() => import("./pages/common/CommonStart"));
const TestChatGPT = lazy(() => import("./pages/common/TestChatGPT"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const SetPassword = lazy(() => import("./pages/auth/SetPassword"));
//noLogin pages
const DemoReport = lazy(() => import("./pages/public/DemoReport"));
const NoLogin = lazy(() => import("./pages/public/PublicMain"));
const QuickReport = lazy(() => import("./pages/common/reports/QuickReport"));
const HiddenReportAcAdva = lazy(() => import("./pages/common/reports/HiddenReportAcAdva"));
const HiddenReportAcBegg = lazy(() => import("./pages/common/reports/HiddenReportAcBegg"));
const HiddenReportAcElite = lazy(() => import("./pages/common/reports/HiddenReportAcElite"));
const HiddenReportInAdva = lazy(() => import("./pages/common/reports/HiddenReportInAdva"));
const HiddenReportInBegg = lazy(() => import("./pages/common/reports/HiddenReportInBegg"));
const HiddenReportInElite = lazy(() => import("./pages/common/reports/HiddenReportInElite"));
const UnSubscribe = lazy(() => import("./pages/common/UnSubscribe"));
const Verify = lazy(() => import("./pages/auth/Verify"));
const OtpVerify = lazy(() => import("./pages/auth/OtpVerify"));
const NotFound = lazy(() => import("./pages/public/NotFound"));

// --- Route Protector ---

interface RouteProtectorProps { children: React.ReactNode; allowedEmails?: string[]; errorMsg?: string; }

const RouteProtector: React.FC<RouteProtectorProps> = ({ children, allowedEmails, errorMsg, }) => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = async () => {
      // 1. Check for stored redirect path
      const redirectPath = localStorage.getItem("redirectPath");
      if (redirectPath) { navigate(redirectPath, { replace: true }); return; }

      // 2. Get user email from token
      const tokenStr = localStorage.getItem("sb-wrllwxiyyswaisrygvay-auth-token");
      let email: string | null = null;
      if (tokenStr) {
        try { const tokenObj = JSON.parse(tokenStr); email = tokenObj?.user?.email?.toLowerCase() || null; }
        catch (e) { console.log("Token invalid", e); }
      }

      if (!email) { navigate("/home", { replace: true }); return; }

      // 3. Email Whitelist Check (for Reports, etc.)
      if (allowedEmails && allowedEmails.length > 0) {
        const allowedList = allowedEmails.map((e) => e.toLowerCase());
        setAllowed(allowedList.includes(email));
        return;
      }

      // 4. Admin Check - Redirect to Admin Dashboard
      if (ADMIN_EMAILS.includes(email)) { navigate("/admin/dashboard", { replace: true }); return; }

      // 5. Role-based Routing (from Database)
      const { data: mdUser } = await supabase.from("MDUser").select("user_type").eq("email", email).maybeSingle();

      if (mdUser?.user_type) {
        // Athlete (1, 2)
        if ([1, 2].includes(mdUser.user_type)) { navigate("/athlete/dashboard", { replace: true }); return; }
        // Academy (3, 4)
        if ([3, 4].includes(mdUser.user_type)) { navigate("/academy/dashboard", { replace: true }); return; }
      }

      // Fallback
      navigate("/home", { replace: true });
    };

    checkAccess();
  }, [allowedEmails, navigate]);

  if (allowed === false) {
    return (
      <div style={{ textAlign: "center", marginTop: 32, color: "red", fontSize: 30 }}>
        {errorMsg || "You do not have permission to access this page."}
      </div>
    );
  }

  return <>{children}</>;
};

// --- App Component ---

const SEOUpdater: React.FC = () => {
  const location = useLocation();
  const canonicalUrl = `https://www.mentaldesire.com${location.pathname}`;
  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:url" content={canonicalUrl} />
    </Helmet>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e: any) => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);

    // Disable certain key combinations
    const handleKeyDown = (e: any) => {
      // F12
      if (e.keyCode === 123) { e.preventDefault(); }
      // Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      if ((e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) || (e.ctrlKey && e.key === "U")) { e.preventDefault(); }
      // Cmd+Opt+I (Mac)
      if ((e.metaKey && e.altKey && e.key === "I")) { e.preventDefault(); }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <span className="unselectable-text">
      {/* Toast notifications */}
      <Toaster position="top-right" toastOptions={{ style: { fontFamily: "Poppins, sans-serif", borderRadius: "8px", background: "var(--background-paper)", color: "var(--neutral-black)", boxShadow: "var(--shadow-md)", }, success: { style: { background: "var(--status-success)", color: "#fff" }, }, error: { style: { background: "var(--status-error)", color: "#fff" } }, }} />
      <HelmetProvider>
        <BrowserRouter>
          <SEOUpdater />
          <Suspense fallback={<div className="loading-spinner"></div>}>
            <Routes>
              {/* No Login Routes */}
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/resetpassword" element={<ResetPassword />} />
              <Route path="/setpassword" element={<SetPassword />} />
              <Route path="/demo-report" element={<DemoReport />} />
              {/* Common Routes */}
              <Route path="/assessment" element={<Start />} />
              <Route path="/assessment/:id" element={<Start />} />
              <Route path="/athlete/*" element={<RouteProtector> <AthleteMain /> </RouteProtector>} />
              <Route path="/athlete" element={<RouteProtector> <Navigate to="/athlete/dashboard" replace /> </RouteProtector>} />
              <Route path="/login" element={<Login />} />
              <Route path="/AthleteRegistration" element={<AthRegi />} />
              <Route path="/manual-assessment" element={<CommonStart />} />
              {/* Academy admin routes */}
              <Route path="/registration" element={<AcademyRegi />} />
              <Route path="/academy" element={<Navigate to="/academy/dashboard" replace />} />
              <Route path="/academy/login" element={<AcademyLogin />} />
              <Route path="/academy/*" element={<AcademyMain />} />
              {/* Super admin routes */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/*" element={<Main />} />
              {/* Catch-all for NoLogin public pages */}
              <Route path="/*" element={<NoLogin />} />
              {/* 404 – fallback for all completely unmatched routes */}
              <Route path="/404" element={<NotFound />} />
              {/* Report route (protected) */}
              <Route path="/report/:id" element={<RouteProtector allowedEmails={["kevalc@eoniansoftware.com"]} errorMsg="You do not have permission to access this report."><Report /></RouteProtector>} />
              <Route path="/quickreport/:id" element={<RouteProtector allowedEmails={["kevalc@eoniansoftware.com"]} errorMsg="You do not have permission to access this quickreport."> <QuickReport /> </RouteProtector>} />
              <Route path="/hiddenreportAcAdva/:id" element={<RouteProtector allowedEmails={["kevalc@eoniansoftware.com"]} errorMsg="You do not have permission to access this hiddenreport."> <HiddenReportAcAdva /> </RouteProtector>} />
              <Route path="/hiddenreportAcBegg/:id" element={<RouteProtector allowedEmails={["kevalc@eoniansoftware.com"]} errorMsg="You do not have permission to access this hiddenreport."> <HiddenReportAcBegg /> </RouteProtector>} />
              <Route path="/hiddenreportAcElite/:id" element={<RouteProtector allowedEmails={["kevalc@eoniansoftware.com"]} errorMsg="You do not have permission to access this hiddenreport."> <HiddenReportAcElite /> </RouteProtector>} />
              <Route path="/hiddenreportInAdva/:id" element={<RouteProtector allowedEmails={["kevalc@eoniansoftware.com"]} errorMsg="You do not have permission to access this hiddenreport."> <HiddenReportInAdva /> </RouteProtector>} />
              <Route path="/hiddenreportInBegg/:id" element={<RouteProtector allowedEmails={["kevalc@eoniansoftware.com"]} errorMsg="You do not have permission to access this hiddenreport."> <HiddenReportInBegg /> </RouteProtector>} />
              <Route path="/hiddenreportInElite/:id" element={<RouteProtector allowedEmails={["kevalc@eoniansoftware.com"]} errorMsg="You do not have permission to access this hiddenreport."> <HiddenReportInElite /> </RouteProtector>} />
              {/* TestChatGPT route (protected) */}
              <Route path="/testChatGPT" element={<RouteProtector allowedEmails={["kevalc@eoniansoftware.com"]}> <TestChatGPT /> </RouteProtector>} />
              <Route path="/unsubscribe/:email" element={<UnSubscribe />} />
              <Route path="/verify/:id/:password/:type?" element={<Verify />} />
              <Route path="/otp-verify" element={<OtpVerify />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </HelmetProvider>
    </span>
  );
};

export default App;
